import { Search } from '@mui/icons-material';
import { Autocomplete, Checkbox, ListItemText, TextField } from '@mui/material';
import { useState } from 'react';

/**
 * Searchbar component with:
 * - Optional suggestions list (from `userSuggestions`)
 * - Controlled dropdown (only opens when matches exist)
 * - Pressing Enter selects an exact match and closes the dropdown
 * - Fully stylized with MUI sx props
 *
 * @param {boolean} showSuggestions - Whether to display user suggestions
 * @param {Array} userSuggestions - List of suggestion objects (must have a `name` field)
 * @param {function} handleInputChange - Called on text change
 * @param {function} handleUserSelect - Called when an option is selected
 */
export default function Searchbar({
  showSuggestions = true,
  userSuggestions,
  handleInputChange,
  handleUserSelect,
}) {
  // State for current text in the input
  const [inputValue, setInputValue] = useState('');
  // Controls the visibility of the dropdown
  const [open, setOpen] = useState(true);

  // Filter suggestions based on input text (case-insensitive)
  const filteredSuggestions = showSuggestions
    ? userSuggestions.filter(option =>
        option.name?.toLowerCase().includes(inputValue.toLowerCase())
      )
    : [];

  return (
    <Autocomplete
      // ---------- Styles ----------
      sx={{
        '& .MuiOutlinedInput-root': {
          borderRadius: '8px',
          backgroundColor: '#F4F4F4',
          padding: '5px',
        },
      }}
      // ---------- Behavior ----------
      open={open && filteredSuggestions.length > 0} // open only if there are matches
      freeSolo={false} // only allow selection from list, no arbitrary text
      popupIcon={null} // remove dropdown arrow icon
      noOptionsText="" // hide "No options" message
      options={filteredSuggestions} // list of matching suggestions
      getOptionLabel={option => `${option.name}`} // how options are displayed
      // When input changes
      onInputChange={(event, value) => {
        setInputValue(value);
        setOpen(true);
        handleInputChange(event, value);
      }}
      // When an option is selected normally (click/tab)
      onChange={handleUserSelect}
      // ---------- Input rendering ----------
      renderInput={params => (
        <TextField
          {...params}
          placeholder="Search"
          variant="outlined"
          fullWidth
          // Custom keyboard handling (for Enter key selection)
          onKeyDown={e => {
            if (e.key === 'Enter') {
              const match = filteredSuggestions.find(
                option =>
                  option.name?.toLowerCase() === inputValue.toLowerCase()
              );
              if (match) {
                handleUserSelect(e, match); // trigger select handler
                setOpen(false); // close dropdown
                e.preventDefault(); // prevent form submit
              }
            }
          }}
          // Add search icon before text
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <Search sx={{ marginRight: '5px' }} />
                {params.InputProps.startAdornment}
              </>
            ),
          }}
        />
      )}
      // ---------- Option rendering ----------
      renderOption={(props, option) => (
        <li {...props}>
          <Checkbox icon={<span />} checkedIcon={<span />} checked={false} />
          <ListItemText primary={`${option.name}`} />
        </li>
      )}
    />
  );
}
